import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Box } from 'reflexbox';
import styled from 'styled-components';
import _ from 'lodash';
import moment from 'moment';

import * as graphql from 'generated/graphql';
import { Button, IconButton, Input, Label, Popover, Separator } from '@treams/treams-ui';
import { ChevronRightIcon } from '@treams/treams-icons';

export type WorkspacePlanView = Pick<
	graphql.WorkspacePlanV2,
	'id' | 'type' | 'startDate' | 'endDate' | 'isTrial' | 'isExpired' | 'minimumUserCount'
>;

const WorkspacePlanContainer = styled(Box)<{ expired?: boolean }>`
	background-color: ${(props) =>
		props.expired ? props.theme.colors.whiteBlue : props.theme.colors.white};
	border: 1px solid ${(props) => props.theme.colors.lightBlue};
	border-radius: 5px;
	align-items: center;
	font-size: 15px;
`;

const WorkspacePlanType = styled.span`
	font-weight: bold;
`;

const TrialBadge = styled(Box)`
	background-color: ${(props) => props.theme.colors.purple};
	border-radius: 3px;
	color: ${(props) => props.theme.colors.white};
	font-size: 13px;
	font-weight: bold;
	padding: 3px 5px;
`;

const EndDate = styled.span<{ expired?: boolean }>`
	color: ${(props) => (props.expired ? props.theme.colors.red : 'unset')};
`;

const CollapseButton = styled(IconButton)<{ isCollaped: boolean }>`
	transform: rotate(${(props) => (props.isCollaped ? '0deg' : '90deg')});
`;

const FormContainer = styled(Flex)<{ expired?: boolean }>`
	pointer-events: ${(props) => (props.expired ? 'none' : 'unset')};
	opacity: ${(props) => (props.expired ? 0.7 : 1)};
`;

const Collapse = styled(Box)<{ isCollapsed: boolean }>`
	overflow: hidden;
	margin: -10px;
	padding: 10px;
	max-height: ${(props) => (props.isCollapsed ? 0 : '200px')};
	transition: max-height 0.2s ease-out;
`;

const LabelButton = styled(Label)<{ enabled: boolean }>`
	color: ${(props) => props.theme.colors.blue};
	transition: opacity 0.1s;
	text-decoration: ${(props) => (props.enabled ? 'underline' : 'none')};
	cursor: ${(props) => (props.enabled ? 'pointer' : 'default')};

	&:hover {
		opacity: ${(props) => (props.enabled ? 0.8 : 1)};
	}
`;

export default function WorkspacePlanCard({
	plan,
	workspace,
	isCollapsed,
	toggleCollapse,
}: {
	plan: WorkspacePlanView;
	workspace: Pick<graphql.Workspace, 'id'>;
	isCollapsed: boolean;
	toggleCollapse: () => void;
}) {
	const { t } = useTranslation('', { keyPrefix: 'workspaces.plans' });
	const { t: gt } = useTranslation();

	const [update] = graphql.useUpdateWorkspacePlanMutation();
	const [remove] = graphql.useRemoveWorkspacePlanMutation({
		update(cache, { data }) {
			if (data?.removeWorkspacePlan.success) {
				if (!isCollapsed) {
					toggleCollapse();
				}

				const id = cache.identify(plan);
				cache.evict({ id });
			}
		},
	});

	const initialStartDate = useMemo(
		() => plan.startDate && new Date(plan.startDate),
		[plan.startDate],
	);
	const initialEndDate = useMemo(() => plan.endDate && new Date(plan.endDate), [plan.endDate]);

	const [startDate, setStartDate] = useState(initialStartDate);
	const [endDate, setEndDate] = useState(initialEndDate);
	const [minimumUserCount, setMinimumUserCount] = useState(plan.minimumUserCount);

	useEffect(() => {
		setStartDate(initialStartDate);
		setEndDate(initialEndDate);
		setMinimumUserCount(plan.minimumUserCount);
	}, [initialEndDate, initialStartDate, plan]);

	const [confirmingRemove, setConfirmingRemove] = useState(false);

	const setEndDateMinStartDate = useCallback(
		(date: Date | undefined) => {
			if (date && date.getTime() < startDate.getTime()) {
				setEndDate(startDate);
			} else {
				setEndDate(date);
			}
		},
		[startDate, setEndDate],
	);

	const handleSave = async () => {
		await update({
			variables: {
				input: {
					workspaceID: workspace.id,
					planID: plan.id,
					startDate,
					endDate,
					isTrial: plan.isTrial,
					minimumUserCount,
				},
			},
		});
	};

	const renew = useCallback(async () => {
		await update({
			variables: {
				input: {
					workspaceID: workspace.id,
					planID: plan.id,
					startDate,
					endDate: moment().add(14, 'days').toDate(),
					isTrial: true,
				},
			},
		});
	}, [update, workspace.id, plan.id, startDate]);

	const toggleTrial = useCallback(async () => {
		const isTrial = !plan.isTrial;

		await update({
			variables: {
				input: {
					workspaceID: workspace.id,
					planID: plan.id,
					startDate,
					endDate: isTrial ? moment().add(14, 'days').toDate() : undefined,
					isTrial,
				},
			},
		});
	}, [update, workspace.id, plan, startDate]);

	const handleRemove = useCallback(async () => {
		await remove({
			variables: {
				input: {
					workspaceID: workspace.id,
					planID: plan.id,
				},
			},
		});
	}, [remove, workspace.id, plan.id]);

	const isDirty =
		startDate?.getTime() !== initialStartDate?.getTime() ||
		endDate?.getTime() !== initialEndDate?.getTime() ||
		minimumUserCount !== plan.minimumUserCount;

	const dateFormat = 'DD/MM/YYYY';

	return (
		<WorkspacePlanContainer expired={plan.isExpired} mb={2} p={3}>
			<Flex alignItems="center">
				<CollapseButton
					icon={<ChevronRightIcon />}
					isCollaped={isCollapsed}
					onClick={toggleCollapse}
					type="secondary"
					small
					mr={3}
				/>
				<WorkspacePlanType>{t(`type.${_.camelCase(plan.type)}`)}</WorkspacePlanType>
				{plan.isTrial && <TrialBadge ml={2}>{t('trial')}</TrialBadge>}
				<Box flexGrow={1} />
				<Label>
					{moment(initialStartDate).format(dateFormat)}
					{' – '}
					<EndDate expired={plan.isExpired}>
						{initialEndDate ? moment(initialEndDate).format(dateFormat) : t('continuous')}
					</EndDate>
				</Label>
			</Flex>
			<Collapse isCollapsed={isCollapsed}>
				<Separator my={3} />
				{plan.isExpired && <Label my={2}>{t('cantEditExpired')}</Label>}
				<FormContainer expired={plan.isExpired}>
					<Box mr={2}>
						<Label>{t('startDate')}</Label>
						<Input.DatePicker small value={startDate} onChange={setStartDate} mt={1} />
					</Box>
					<Box>
						<Flex justifyContent="space-between">
							<Label>{t('endDate')}</Label>
							<LabelButton onClick={() => setEndDateMinStartDate(undefined)} enabled={!!endDate}>
								{t('continuous')}
							</LabelButton>
						</Flex>
						<Input.DatePicker small value={endDate} onChange={setEndDateMinStartDate} mt={1} />
					</Box>
					<Separator vertical mx={3} mt={1} />
					<Box flexShrink={0}>
						<Label>{gt('settings.workspaces.edit.plan.minimumLicenses.title')}</Label>
						<Input
							small
							type="number"
							value={minimumUserCount.toString()}
							onChange={(value) => setMinimumUserCount(parseInt(value))}
							mt={1}
						/>
					</Box>
				</FormContainer>
				<Flex mt={4} justifyContent="flex-end" alignItems="center">
					<Button
						small
						title={t(plan.isTrial ? 'upgrade' : 'downgrade')}
						type="plain"
						onClick={() => toggleTrial()}
						disabled={isDirty}
						mr={2}
					/>
					<Popover.Confirm
						visible={confirmingRemove}
						title={t('remove.confirm.title')}
						confirmTitle={t('remove.confirm.yes')}
						cancelTitle={t('remove.confirm.no')}
						// TODO: Add extra info string to Locize
						extraInfo={t('remove.confirm.details')}
						onCancel={() => setConfirmingRemove(false)}
						onConfirm={async () => {
							await handleRemove();
							setConfirmingRemove(false);
						}}
						confirmType="destructive"
					>
						<Button
							small
							title={gt('common.delete')}
							type="destructive"
							onClick={() => setConfirmingRemove(true)}
							mr={2}
						/>
					</Popover.Confirm>
					{plan.isExpired ? (
						<Button small title={t('renew')} onClick={renew} type="primary" />
					) : (
						<Button
							small
							title={gt('common.save')}
							onClick={handleSave}
							type="primary"
							disabled={!isDirty}
						/>
					)}
				</Flex>
			</Collapse>
		</WorkspacePlanContainer>
	);
}
